<template>
  <div class="header-nav-overlay" aria-hidden="true">
    <div class="header-nav-overlay__cover" :style="coverStyle" />
    <div
      v-for="item in items"
      :key="item.label"
      class="header-nav-overlay__item"
      :style="itemStyle(item)"
    >
      {{ item.label }}
    </div>
    <div
      v-for="action in actions"
      :key="`action-${action.label}`"
      :class="[
        'header-nav-overlay__action',
        { 'header-nav-overlay__action--breathing': action.label === 'StoReel Canvas' },
      ]"
      :style="actionStyle(action)"
    >
      {{ action.label }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'

import type { HeaderNavItem } from '../../data/home2026'

const props = defineProps<{
  items: HeaderNavItem[]
  actions: HeaderNavItem[]
  scaledPx: (value: number) => string
}>()

const navCover = { x: 620, y: 30, w: 660, h: 60 }

const coverStyle = computed<CSSProperties>(() => ({
  left: props.scaledPx(navCover.x),
  top: props.scaledPx(navCover.y),
  width: props.scaledPx(navCover.w),
  height: props.scaledPx(navCover.h),
}))

const itemStyle = (item: HeaderNavItem): CSSProperties => ({
  left: props.scaledPx(item.x),
  top: props.scaledPx(item.y),
  width: props.scaledPx(item.w),
  height: props.scaledPx(item.h),
  borderRadius: props.scaledPx(8),
  fontSize: props.scaledPx(14),
  lineHeight: props.scaledPx(20),
})

const actionStyle = (item: HeaderNavItem): CSSProperties => ({
  ...itemStyle(item),
  borderRadius: props.scaledPx(42),
  borderWidth: props.scaledPx(0.5),
})
</script>

<style scoped>
.header-nav-overlay {
  position: absolute;
  inset: 0;
  z-index: 4;
  font-family: 'Alibaba PuHuiTi 3.0', AlibabaPuHuiTi_3_55_Regular, PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-synthesis: none;
  pointer-events: none;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricprecision;
}

.header-nav-overlay__cover,
.header-nav-overlay__item,
.header-nav-overlay__action {
  position: absolute;
  box-sizing: border-box;
}

.header-nav-overlay__cover {
  background: #000;
}

.header-nav-overlay__item {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #202020;
  color: #fff;
  font-family: AlibabaPuHuiTi_3_65_Medium, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-weight: 500;
  letter-spacing: 0;
  white-space: nowrap;
}

.header-nav-overlay__action {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-style: solid;
  border-color: rgb(0 201 255 / 84%);
  background: rgb(12 80 72 / 96%);
  box-shadow: inset 0 0 18px rgb(146 254 157 / 15%);
  color: #fff;
  font-family: AlibabaPuHuiTi_3_65_Medium, 'Alibaba PuHuiTi 3.0', PingFangSC-Regular, Source Han Sans CN, sans-serif;
  font-weight: 500;
  letter-spacing: 0;
  white-space: nowrap;
  transform-origin: center;
}

.header-nav-overlay__action--breathing {
  animation: canvas-action-breathe 1.8s ease-in-out infinite;
  will-change: transform, filter, box-shadow;
}

@keyframes canvas-action-breathe {
  0%,
  100% {
    box-shadow:
      inset 0 0 18px rgb(146 254 157 / 15%),
      0 0 0 rgb(0 201 255 / 0%),
      0 0 8px rgb(146 254 157 / 18%);
    filter: brightness(1);
    transform: scale(1);
  }

  50% {
    box-shadow:
      inset 0 0 22px rgb(146 254 157 / 32%),
      0 0 10px rgb(0 201 255 / 45%),
      0 0 22px rgb(146 254 157 / 34%);
    filter: brightness(1.18);
    transform: scale(1.04);
  }
}

@media (prefers-reduced-motion: reduce) {
  .header-nav-overlay__action--breathing {
    animation: none;
  }
}
</style>
